import { BookingType } from "../../../features/Booking-Form/typings/booking-inputs";
import { compareBookingTypeAndPlaceType } from "../Components/Views/SelectFloorPlanView/Functions/SelectFloorPlanView.functions";
import { IPlaceSchedule } from "../Domain/Types/FloorPlan/PlaceSchedule";
import { PlaceVariant } from "../Domain/Types/FloorPlan/PlaceVariant.type";
import { ISeatStatus } from "../Domain/Types/FloorPlan/SeatStatus.type";
import { IZoneSchedule } from "../Domain/Types/FloorPlan/ZoneSchedule";
import { ZoneVariant } from "../Domain/Types/FloorPlan/ZoneVariant.type";

function bookingObjectInventoryId(
  workplace: IPlaceSchedule | undefined,
  zone: IZoneSchedule | undefined
): number | undefined {
  return (
    workplace?.inventoryId || workplace?.inventory?.id || zone?.inventory?.id || zone?.inventoryId
  );
}

function listHasId(list: number[] | undefined, inventoryId: number | undefined): boolean {
  return inventoryId != null && (list?.includes(inventoryId) ?? false);
}

/**
 * Painted colour only (forbidden → occupied → blocked → own occupied → disabled → available).
 * Do not use for Confirm; that stays on generatePlaceVariant + selectableByVariant.
 */
export function resolveDisplayPlaceVariant(
  seatStatus: ISeatStatus,
  inventoryId: number | undefined
): PlaceVariant | undefined {
  if (inventoryId == null) {
    return undefined;
  }
  if (listHasId(seatStatus.restrictedList, inventoryId)) {
    return PlaceVariant.FORBIDDEN;
  }
  if (listHasId(seatStatus.partialList, inventoryId)) {
    return PlaceVariant.PARTIAL;
  }
  if (listHasId(seatStatus.occupiedList, inventoryId)) {
    return PlaceVariant.OCCUPIED;
  }
  if (listHasId(seatStatus.blockedList, inventoryId)) {
    return PlaceVariant.BLOCKED;
  }
  if (listHasId(seatStatus.userOccupiedList, inventoryId)) {
    return PlaceVariant.OCCUPIED;
  }
  if (listHasId(seatStatus.disableList, inventoryId)) {
    return PlaceVariant.WEAK;
  }
  if (listHasId(seatStatus.availableList, inventoryId)) {
    return PlaceVariant.AVAILABLE;
  }
  if (listHasId(seatStatus.tempList, inventoryId)) {
    return PlaceVariant.TEMP;
  }
  return undefined;
}

function resolveDisplayZoneVariant(
  seatStatus: ISeatStatus,
  inventoryId: number | undefined
): ZoneVariant {
  if (listHasId(seatStatus.restrictedList, inventoryId)) {
    return ZoneVariant.FORBIDDEN;
  }
  if (listHasId(seatStatus.occupiedList, inventoryId)) {
    return ZoneVariant.OCCUPIED;
  }
  if (listHasId(seatStatus.blockedList, inventoryId)) {
    return ZoneVariant.BLOCKED;
  }
  if (listHasId(seatStatus.userOccupiedList, inventoryId)) {
    return ZoneVariant.OCCUPIED;
  }
  if (listHasId(seatStatus.disableList, inventoryId)) {
    return ZoneVariant.DISABLED;
  }
  if (listHasId(seatStatus.availableList, inventoryId)) {
    return ZoneVariant.AVAILABLE;
  }
  if (listHasId(seatStatus.tempList, inventoryId)) {
    return ZoneVariant.TEMP;
  }
  return ZoneVariant.DISABLED;
}

export const generatePlaceVariant = (
  { workplace, zone }: { workplace: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined },
  seatStatus: ISeatStatus,
  disabled?: boolean,
  bookingType?: BookingType | null
): PlaceVariant => {
  const { availableList, occupiedList, blockedList, disableList, restrictedList, tempList } =
    seatStatus;

  const wpTypeId = workplace?.inventory?.placeTypeId || workplace?.placeTypeId;
  const znTypeId = zone?.inventory?.zoneTypeId || zone?.inventory?.zoneType?.id || zone?.zoneTypeId;
  const inventoryId = bookingObjectInventoryId(workplace, zone);

  const checkType = compareBookingTypeAndPlaceType(bookingType, {
    placeTypeId: wpTypeId,
    zoneTypeId: znTypeId
  });

  if (!disabled && checkType && inventoryId) {
    if (restrictedList?.includes(inventoryId)) return PlaceVariant.FORBIDDEN;
    if (occupiedList?.includes(inventoryId)) return PlaceVariant.OCCUPIED;
    if (blockedList?.includes(inventoryId)) return PlaceVariant.BLOCKED;
    if (availableList?.includes(inventoryId)) return PlaceVariant.AVAILABLE;
    if (disableList?.includes(inventoryId)) return PlaceVariant.WEAK;
    if (tempList?.includes(inventoryId)) return PlaceVariant.TEMP;
  }
  return PlaceVariant.WEAK;
};

export const generateZoneVariant = (zone: IZoneSchedule, seatStatus: ISeatStatus): ZoneVariant => {
  const zoneInvId = zone.inventory?.id || zone.inventoryId;
  const zoneTypeId =
    zone?.inventory?.zoneTypeId || zone?.inventory?.zoneType?.id || zone?.zoneTypeId;

  if (zoneTypeId !== 3) return ZoneVariant.AVAILABLE;

  return resolveDisplayZoneVariant(seatStatus, zoneInvId);
};

/**
 * Display colour for a booking object on the floor plan (not the Confirm gate).
 */
export const generateBookingPlaceVariant = (
  { workplace, zone }: { workplace: IPlaceSchedule | undefined; zone: IZoneSchedule | undefined },
  seatStatus: ISeatStatus,
  bookingType?: BookingType | null
): PlaceVariant => {
  const wpTypeId = workplace?.inventory?.placeTypeId || workplace?.placeTypeId;
  const znTypeId = zone?.inventory?.zoneTypeId || zone?.inventory?.zoneType?.id || zone?.zoneTypeId;
  const inventoryId = bookingObjectInventoryId(workplace, zone);

  const checkType = compareBookingTypeAndPlaceType(bookingType, {
    placeTypeId: wpTypeId,
    zoneTypeId: znTypeId
  });

  if (!checkType || !inventoryId) return PlaceVariant.WEAK;

  return resolveDisplayPlaceVariant(seatStatus, inventoryId) ?? PlaceVariant.WEAK;
};
